/**
 * ACL Provider with TanStack Query Integration
 * Provides access control list data throughout the application
 */
import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  PropsWithChildren,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  NavigationItem,
  UserAccessControlList,
} from '@/shared/types/acl';
import { aclService } from '@/app/services/acl';
import { useAclStore } from '@/app/stores/aclStore';
import { refreshCsrfTokenIfExpired } from '@/app/utils/csrfToken';
import { useAuth } from './auth';
import { MODULE_ROUTES } from '@/app/constants/moduleRoutes';

/**
 * ACL Context interface
 */
export interface AclContextValue {
  /** Complete ACL data */
  acl: UserAccessControlList | null;
  /** Navigation items derived from active, visible modules */
  navigationModules: NavigationItem[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch ACL data */
  refetch: () => Promise<void>;
}

const AclContext = createContext<AclContextValue | null>(null);

/**
 * Hook to use ACL context
 */
export function useAcl(): AclContextValue {
  const context = useContext(AclContext);

  if (!context) {
    throw new Error('useAcl must be used within AclProvider');
  }

  return context;
}

/**
 * Transform active modules to NavigationItem array
 */
function transformModulesToNavigationItems(
  acl: UserAccessControlList
): NavigationItem[] {
  return acl.modules
    .filter((m) => m.isActive && !m.isHidden)
    .sort((a, b) => a.order - b.order)
    .map((m) => ({
      id: m.id,
      title: m.name,
      code: m.code,
      url: MODULE_ROUTES[m.code],
      is_visible: !!MODULE_ROUTES[m.code] && !m.isHidden,
      order: m.order,
    }));
}

/**
 * ACL Provider Component
 * Should be wrapped inside AuthProvider to access user authentication state
 */
export function AclProvider({ children }: PropsWithChildren) {
  const auth = useAuth();

  const {
    data: acl,
    isLoading,
    error,
    refetch: refetchQuery,
  } = useQuery({
    queryKey: ['acl'],
    queryFn: async () => {
      return await aclService.getAccessControlList();
    },
    enabled: auth.isAuthenticated,
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const navigationModules = useMemo(() => {
    if (!acl) return [];
    return transformModulesToNavigationItems(acl);
  }, [acl]);

  const setUserInfo = useAclStore((state) => state.setUserInfo);

  useEffect(() => {
    setUserInfo(acl?.user_info ?? null);
  }, [acl?.user_info, setUserInfo]);

  // Bootstrap CSRF token once the user is authenticated and ACL data is loaded.
  // This ensures every subsequent mutating API request has a valid token available.
  useEffect(() => {
    if (acl) {
      void refreshCsrfTokenIfExpired();
    }
  }, [acl]);

  const refetch = useCallback(async () => {
    await refetchQuery();
  }, [refetchQuery]);

  const value = useMemo<AclContextValue>(
    () => ({
      acl: acl ?? null,
      navigationModules,
      isLoading,
      error: error as Error,
      refetch,
    }),
    [acl, navigationModules, isLoading, error, refetch]
  );

  return <AclContext.Provider value={value}>{children}</AclContext.Provider>;
}
